import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { SiteHealth } from "@/pages/staff/SiteHealth";
import { DemoMembers } from "@/pages/staff/DemoMembers";
import { FreshStart } from "@/pages/staff/FreshStart";

const SettingRow = ({ s, onSave }) => {
  const initial = s.type === "list" ? (s.value || []).join(", ") : s.type === "json" ? JSON.stringify(s.value, null, 1) : s.value;
  const [val, setVal] = useState(initial);
  const [dirty, setDirty] = useState(false);
  const id = `setting-${s.key}`;
  const change = (v) => { setVal(v); setDirty(true); };
  const save = () => {
    let value = val;
    if (s.type === "json") { try { value = JSON.parse(val); } catch { toast.error("That is not valid JSON."); return; } }
    onSave({ key: s.key, value, clear_placeholder: s.is_placeholder && !String(val).includes("PLACEHOLDER") });
    setDirty(false);
  };
  return (
    <div className="grid gap-3 py-5 lg:grid-cols-12 lg:items-start" data-testid={`setting-${s.key}`}>
      <div className="lg:col-span-5">
        <label htmlFor={id} className="label mb-0">{s.label} {s.is_placeholder && <span className="ml-2 rounded border border-sage-400 px-2 py-0.5 text-xs font-normal text-ink-500" data-testid={`placeholder-badge-${s.key}`}>Placeholder</span>}</label>
        {s.description && <p className="help">{s.description}</p>}
        <p className="help font-mono">{s.key}</p>
      </div>
      <div className="lg:col-span-7 flex items-start gap-3">
        {s.type === "bool" ? (
          <div className="flex items-center gap-3 min-h-[44px]"><Switch id={id} checked={!!val} onCheckedChange={(v) => { change(v); onSave({ key: s.key, value: v }); }} data-testid={`setting-switch-${s.key}`} /><span>{val ? "On" : "Off"}</span></div>
        ) : s.type === "json" ? (
          <textarea id={id} className="input font-mono text-sm min-h-[100px]" value={val} onChange={(e) => change(e.target.value)} data-testid={`setting-input-${s.key}`} />
        ) : (
          <input id={id} type={s.type === "int" ? "number" : "text"} className="input" value={val ?? ""} onChange={(e) => change(e.target.value)} data-testid={`setting-input-${s.key}`} />
        )}
        {s.type !== "bool" && <button type="button" className="btn-primary" disabled={!dirty} onClick={save} data-testid={`setting-save-${s.key}`}>Save</button>}
      </div>
    </div>
  );
};

export default function StaffSettings() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isSysAdmin = user.roles.includes("system_admin");
  const { data: settings = [] } = useQuery({ queryKey: ["admin", "settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  const { data: flags = [] } = useQuery({ queryKey: ["admin", "flags"], queryFn: async () => (await api.get("/admin/feature-flags")).data });
  const saveSetting = useMutation({ mutationFn: ({ key, ...body }) => api.put(`/admin/settings/${key}`, body), onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin"] }); qc.invalidateQueries({ queryKey: ["content"] }); toast.success("Setting saved."); }, onError: (e) => toast.error(apiError(e)) });
  const saveFlag = useMutation({ mutationFn: ({ key, enabled }) => api.put(`/admin/feature-flags/${key}`, { enabled }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin"] }); toast.success("Flag updated."); }, onError: (e) => toast.error(apiError(e)) });
  const groups = Array.from(new Set(settings.map((s) => s.group)));

  return (
    <div data-testid="staff-settings">
      <h1 className="text-4xl">Settings & Feature Flags</h1>
      <p className="mt-3 text-ink-500 max-w-[65ch]">Admin-editable defaults from the specification. Placeholders are values the spec left bracketed; replacing one clears the badge.</p>

      <section className="mt-10" aria-labelledby="flags-heading">
        <h2 id="flags-heading" className="text-2xl">Feature Flags</h2>
        {!isSysAdmin && <p className="help">Only a System Admin can change flags.</p>}
        <ul className="mt-4 divide-y divide-sage-400/50 border-y border-sage-400/50">
          {flags.map((f) => (
            <li key={f.key} className="flex items-center justify-between gap-4 py-4" data-testid={`flag-${f.key}`}>
              <div><p className="font-semibold">{f.label}</p>{f.description && <p className="help">{f.description}</p>}<p className="help font-mono">{f.key}</p></div>
              <div className="flex items-center gap-3"><Switch checked={f.enabled} disabled={!isSysAdmin} onCheckedChange={(v) => saveFlag.mutate({ key: f.key, enabled: v })} aria-label={f.label} data-testid={`flag-switch-${f.key}`} /><span className="w-8 text-sm">{f.enabled ? "On" : "Off"}</span></div>
            </li>
          ))}
        </ul>
      </section>

      <SiteHealth />
      {isSysAdmin && <DemoMembers />}

      {groups.map((g) => (
        <section key={g} className="mt-12" aria-labelledby={`group-${g}`}>
          <h2 id={`group-${g}`} className="text-2xl">{g}</h2>
          <div className="mt-2 divide-y divide-sage-400/50 border-y border-sage-400/50">
            {settings.filter((s) => s.group === g).map((s) => <SettingRow key={`${s.key}-${JSON.stringify(s.value)}`} s={s} onSave={(body) => saveSetting.mutate(body)} />)}
          </div>
        </section>
      ))}
      {isSysAdmin && <FreshStart />}
    </div>
  );
}
